import {useMemo, useState, type KeyboardEvent} from "react";
import {ChevronsLeft, ChevronsRight, Search} from "lucide-react";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";

export type AdminDualListOption = {
  id: number;
  label: string;
};

type Props = {
  options: AdminDualListOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  availableTitle?: string;
  selectedTitle?: string;
  searchPlaceholder?: string;
  className?: string;
};

/**
 * Dual-list transfer for large option sets (e.g. blocked brands).
 * Presentation-only — parent owns selected ids.
 */
export function AdminDualList({
  options,
  selectedIds,
  onChange,
  availableTitle = "Available",
  selectedTitle = "Selected",
  searchPlaceholder = "Search…",
  className
}: Props) {
  const [q, setQ] = useState("");
  const [pickLeft, setPickLeft] = useState<number[]>([]);
  const [pickRight, setPickRight] = useState<number[]>([]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const sorted = useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, {sensitivity: "base"})),
    [options]
  );
  const query = q.trim().toLowerCase();

  const available = useMemo(
    () => sorted.filter((o) => !selectedSet.has(o.id) && (!query || o.label.toLowerCase().includes(query))),
    [sorted, selectedSet, query]
  );
  const selected = useMemo(
    () => sorted.filter((o) => selectedSet.has(o.id) && (!query || o.label.toLowerCase().includes(query))),
    [sorted, selectedSet, query]
  );

  function moveToSelected(ids: number[]) {
    if(!ids.length) return;
    const next = new Set(selectedIds);
    ids.forEach((id) => next.add(id));
    onChange([...next]);
    setPickLeft([]);
  }

  function moveToAvailable(ids: number[]) {
    if(!ids.length) return;
    const drop = new Set(ids);
    onChange(selectedIds.filter((id) => !drop.has(id)));
    setPickRight([]);
  }

  function onListKey(
    e: KeyboardEvent<HTMLSelectElement>,
    side: "left" | "right",
    visible: AdminDualListOption[]
  ) {
    if(e.key === "Enter" || (e.key === "ArrowRight" && side === "left")) {
      e.preventDefault();
      if(side === "left") moveToSelected(pickLeft.length ? pickLeft : visible.map((v) => v.id).slice(0, 1));
    }
    if((e.key === "Enter" && side === "right") || (e.key === "ArrowLeft" && side === "right")) {
      e.preventDefault();
      moveToAvailable(pickRight.length ? pickRight : visible.map((v) => v.id).slice(0, 1));
    }
  }

  return (
    <div className={cn("admin-dual-list", className)}>
      <div className="admin-dual-list__toolbar">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-11 pl-9"
            placeholder={searchPlaceholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <p className="admin-dual-list__summary tabular-nums">
          Selected {selectedIds.length} · Showing {available.length + selected.length}
        </p>
        {selectedIds.length ? (
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange([])}>
            Clear all
          </Button>
        ) : null}
      </div>

      <div className="admin-dual-list__panels">
        <div className="admin-dual-list__panel">
          <div className="admin-dual-list__panel-head">
            <span>{availableTitle}</span>
            <span className="tabular-nums text-muted-foreground">{available.length}</span>
          </div>
          <select
            multiple
            className="admin-dual-list__select"
            value={pickLeft.map(String)}
            onChange={(e) => setPickLeft(Array.from(e.target.selectedOptions).map((o) => Number(o.value)))}
            onDoubleClick={() => moveToSelected(pickLeft)}
            onKeyDown={(e) => onListKey(e, "left", available)}
            aria-label={availableTitle}
          >
            {available.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <div className="admin-dual-list__panel-actions">
            <Button type="button" size="sm" variant="outline" onClick={() => moveToSelected(available.map((o) => o.id))} disabled={!available.length}>
              Select all
            </Button>
          </div>
        </div>

        <div className="admin-dual-list__transfer">
          <Button type="button" size="icon" variant="outline" aria-label="Add selected" onClick={() => moveToSelected(pickLeft)} disabled={!pickLeft.length}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
          <Button type="button" size="icon" variant="outline" aria-label="Remove selected" onClick={() => moveToAvailable(pickRight)} disabled={!pickRight.length}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
        </div>

        <div className="admin-dual-list__panel">
          <div className="admin-dual-list__panel-head">
            <span>{selectedTitle}</span>
            <span className="tabular-nums text-muted-foreground">{selected.length}</span>
          </div>
          <select
            multiple
            className="admin-dual-list__select"
            value={pickRight.map(String)}
            onChange={(e) => setPickRight(Array.from(e.target.selectedOptions).map((o) => Number(o.value)))}
            onDoubleClick={() => moveToAvailable(pickRight)}
            onKeyDown={(e) => onListKey(e, "right", selected)}
            aria-label={selectedTitle}
          >
            {selected.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <div className="admin-dual-list__panel-actions">
            <Button type="button" size="sm" variant="outline" onClick={() => moveToAvailable(selected.map((o) => o.id))} disabled={!selected.length}>
              Deselect all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
